import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  type SxProps,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useAnimate } from "framer-motion";
import { useSnackbar } from "notistack";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useEffect,
  type Ref,
} from "react";

import { FrameEditorProvider } from "./context/FrameEditorProvider";
import { useFrameEditorContext } from "./context/useFrameEditorContext";
import type { Frame, FrameVariant, OffersConfig } from "@/types/dragon-tiles";

import { useLinkedBroadcasts } from "@/api/broadcastV2Audiences";
import { liveEmailPreviewQueryOptions } from "@/api/dragon-tiles";
import { type TestEmailPayload } from "@/api/dragon-tiles/email";
import { siteQueryOptions } from "@/api/siteDetails";
import { LinkedBroadcastsModalTable } from "@/components/LinkedBroadcastsModalTable";
import { LinkedBroadcastsSaveConfirmModal } from "@/components/LinkedBroadcastsSaveConfirmModal";
import {
  LeaveConfirmDialog,
  NavigationBlockerModal,
} from "@/components/NavigationBlockerModal/NavigationBlockerModal";
import { TestEmailBar } from "@/components/TestEmailBar/TestEmailBar";
import {
  OuterWrapper,
  TopBarWrapper,
  TopBar,
  Title,
  IframePreview,
} from "@/features/DragonTiles/components/FrameEditPanel.styles";
import {
  InlineLexicalInput,
  useInlineOffers,
} from "@/features/TextComposerV2/InlineLexicalInput";
import {
  buildFrameVariantConfig,
  emailVariantPreviewRequest,
  getEmailVariantPreviewHtml,
} from "@/frames/service";
import { isBlankText } from "@/frames/subjectLine";
import { useOfferBlockOffers } from "@/grapesPlugins/email/OfferBlock/hooks";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useTestEmail } from "@/hooks/useTestEmail";
import { CloseIcon, EditOutlinedIcon } from "@/icons";
import { getBackendErrorMessage } from "@/utils/backendError";
import { buildPreviewOffersConfig } from "@/utils/offerTagUtils";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

interface FrameEditorWrapperProps {
  frame: Frame;
  siteId: string;
  onClose?: () => void;
  closeLink?: string;
  closeLinkParams?: Record<string, string>;
  title?: string;
  showReplaceButton?: boolean;
  onReplaceClick?: () => void;
  onFullEditorClick?: () => void;
  /**
   * Called with the server's copy after a subject/preheader save, so a page
   * holding its own copy of the tile can keep it current.
   */
  onFrameSaved?: (frame: Frame) => void;
  replaceButtonText?: string;
  className?: string;
  outerWrapperStyles?: SxProps;
  onSave?: (frame: Frame, updatedVariants: FrameVariant[]) => Frame;
  hideCloseButton?: boolean;
  selectedDevice?: "desktop" | "mobile";
  // Optional overrides for test email functionality
  testEmailPayloadOverrides?: Partial<TestEmailPayload>;
  showFullEditorButton?: boolean;
  showSubjectAndPreHeaderInputs?: boolean;
  showTestEmailButton?: boolean;
  currentBroadcastId?: string;
}

export interface FrameEditorHandle {
  // Resolves true to continue the broadcast save, false if the user cancelled.
  flushPendingSubjectPreheader: () => Promise<boolean>;
  // Before swapping this tile out of the editor (e.g. switching A/B tabs).
  // Prompts like leaving the page; resolves true to go ahead, discarding any
  // unsaved subject/preheader edits.
  confirmLeave: () => Promise<boolean>;
  flagBlankSubjectLine: () => void;
}

const UNSAVED_SL_PH_LEAVE_COPY = {
  title: "Unsaved subject line / preheader",
  content:
    "You have unsaved subject line or preheader changes. Leave without saving?",
};

type FlushChoice = "save" | "keep" | "cancel";

const CloseButton = ({
  hideCloseButton,
  closeLink,
  closeLinkParams,
  onClose,
}: {
  hideCloseButton?: boolean;
  closeLink?: string;
  closeLinkParams?: Record<string, string>;
  onClose?: () => void;
}) => {
  if (hideCloseButton) {
    return null;
  }

  if (closeLink) {
    return (
      <Link
        to={closeLink}
        params={closeLinkParams}
        inactiveProps={{ className: "frame-editor-topbar_close" }}
        activeProps={{
          className:
            "frame-editor-topbar_close frame-editor-topbar_close__active",
        }}
      >
        <CloseIcon fontSize="small" />
      </Link>
    );
  }

  return (
    <Button
      onClick={onClose}
      sx={{ minWidth: "auto", p: 2 }}
      className="frame-editor-topbar_close"
    >
      <CloseIcon fontSize="small" />
    </Button>
  );
};

type SubjectPreheaderChange = { subjectLine?: string; previewText?: string };

function FrameEditorContent({
  frame,
  onClose,
  closeLink,
  closeLinkParams,
  title,
  showReplaceButton = false,
  onReplaceClick,
  onFullEditorClick,
  onFrameSaved,
  replaceButtonText = "Replace tile",
  className,
  outerWrapperStyles,
  hideCloseButton = false,
  selectedDevice,
  testEmailPayloadOverrides,
  showFullEditorButton = false,
  showSubjectAndPreHeaderInputs = true,
  showTestEmailButton = true,
  currentBroadcastId,
  flushRef,
}: FrameEditorWrapperProps & { flushRef?: Ref<FrameEditorHandle> }) {
  const { state, handleSaveWithFrame, siteId, dispatch, isPending } =
    useFrameEditorContext();

  const { data: linkedBroadcasts = [] } = useLinkedBroadcasts(siteId, frame.id);
  // Exclude the broadcast currently being edited: saving SL/PH obviously
  // affects it, so only warn about *other* broadcasts sharing this tile.
  const otherLinkedBroadcasts = useMemo(
    () => linkedBroadcasts.filter((b) => b.id !== currentBroadcastId),
    [linkedBroadcasts, currentBroadcastId],
  );
  const [isLinkedBroadcastsModalOpen, setIsLinkedBroadcastsModalOpen] =
    useState(false);
  const [flushConfirmOpen, setFlushConfirmOpen] = useState(false);
  const flushChoiceRef = useRef<((choice: FlushChoice) => void) | null>(null);
  // Holds the resolve/reject of the InlineLexicalInput save promise plus the
  // pending subject/preheader values while the confirm modal is open.
  const pendingSaveRef = useRef<{
    resolve: () => void;
    reject: (e?: unknown) => void;
    change: SubjectPreheaderChange;
  } | null>(null);

  // The tile's saved copy, kept current by the provider after each save. The
  // field state below starts from it and then holds the user's edits.
  const savedSubjectLine =
    (state.selectedVariant.config?.subject_line as string) || "";
  const savedPreviewText =
    (state.selectedVariant.config?.preview_text as string) || "";
  const [subjectLine, setSubjectLine] = useState(savedSubjectLine);
  const [previewText, setPreviewText] = useState(savedPreviewText);
  const [isSubjectLineFlagged, setIsSubjectLineFlagged] = useState(false);
  const showSubjectLineError = isSubjectLineFlagged && isBlankText(subjectLine);
  const [subjectLineScope, animateSubjectLine] = useAnimate<HTMLDivElement>();
  const theme = useTheme();
  const [isSavingField, setIsSavingField] = useState(false);

  const hasUnsavedSubjectPreheader =
    showSubjectAndPreHeaderInputs &&
    (subjectLine !== savedSubjectLine || previewText !== savedPreviewText);

  const inlineOffers = useInlineOffers(siteId);

  const inputRowStyles: SxProps = {
    display: "flex",
    gap: 3,
    alignItems: "center",
    width: "100%",
  };

  const DEBOUNCE_MS = 600;
  const debouncedSubjectLine = useDebouncedValue(subjectLine, DEBOUNCE_MS);
  const debouncedPreviewText = useDebouncedValue(previewText, DEBOUNCE_MS);

  const performSaveField = useCallback(
    async (change: SubjectPreheaderChange) => {
      const variants = state.frame.frame_variants ?? [];
      const emailIdx = variants.findIndex((v) => v.medium === "email");
      if (emailIdx === -1) return;

      const emailVariant = variants[emailIdx];
      const updatedVariants = [...variants];
      updatedVariants[emailIdx] = {
        ...emailVariant,
        // Merge only the saved field onto the existing config.
        config: buildFrameVariantConfig({
          existing: emailVariant.config,
          ...change,
        }),
      };

      const nextFrame: Frame = {
        ...state.frame,
        frame_variants: updatedVariants,
      };

      dispatch({
        type: "UPDATE_FRAME",
        payload: { frame_variants: nextFrame.frame_variants },
      });

      let savedFrame: Frame;
      try {
        savedFrame = await handleSaveWithFrame(nextFrame);
      } catch (error) {
        enqueueErrorSnackbar(error, "Failed to save changes");
        throw error;
      }
      onFrameSaved?.(savedFrame);
    },
    [state.frame, dispatch, handleSaveWithFrame, onFrameSaved],
  );

  // When the tile is linked to broadcasts, defer the save behind a confirmation
  // modal (saving pushes the SL/PH change into those broadcasts too). The
  // returned promise resolves on confirm and rejects on cancel, so the inline
  // Save button keeps its spinner during the modal and stays dirty on cancel.
  const saveField = useCallback(
    (change: SubjectPreheaderChange) => {
      if (otherLinkedBroadcasts.length > 0) {
        return new Promise<void>((resolve, reject) => {
          pendingSaveRef.current = { resolve, reject, change };
          setIsLinkedBroadcastsModalOpen(true);
        });
      }
      return performSaveField(change);
    },
    [otherLinkedBroadcasts.length, performSaveField],
  );

  const resolveFlushChoice = useCallback((choice: FlushChoice) => {
    setFlushConfirmOpen(false);
    flushChoiceRef.current?.(choice);
    flushChoiceRef.current = null;
  }, []);

  const flushPendingSubjectPreheader = useCallback(async () => {
    if (!hasUnsavedSubjectPreheader) return true;
    const choice = await new Promise<FlushChoice>((resolve) => {
      flushChoiceRef.current = resolve;
      setFlushConfirmOpen(true);
    });
    if (choice === "cancel") return false;
    if (choice === "keep") {
      // Show what will actually be sent.
      setSubjectLine(savedSubjectLine);
      setPreviewText(savedPreviewText);
      return true;
    }
    // The flush dialog already listed the other broadcasts sharing this tile,
    // so save directly rather than through the linked-broadcasts modal.
    await performSaveField({ subjectLine, previewText });
    return true;
  }, [
    hasUnsavedSubjectPreheader,
    performSaveField,
    subjectLine,
    previewText,
    savedSubjectLine,
    savedPreviewText,
  ]);

  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const leaveChoiceRef = useRef<((leave: boolean) => void) | null>(null);

  const resolveLeaveChoice = useCallback((leave: boolean) => {
    setLeaveConfirmOpen(false);
    leaveChoiceRef.current?.(leave);
    leaveChoiceRef.current = null;
  }, []);

  const confirmLeave = useCallback(async () => {
    if (!hasUnsavedSubjectPreheader) return true;
    const leave = await new Promise<boolean>((resolve) => {
      leaveChoiceRef.current = resolve;
      setLeaveConfirmOpen(true);
    });
    if (leave) {
      setSubjectLine(savedSubjectLine);
      setPreviewText(savedPreviewText);
    }
    return leave;
  }, [hasUnsavedSubjectPreheader, savedSubjectLine, savedPreviewText]);

  const flagBlankSubjectLine = useCallback(() => {
    const subjectLineRow = subjectLineScope.current;
    if (!subjectLineRow || !isBlankText(savedSubjectLine)) return;
    setIsSubjectLineFlagged(true);
    subjectLineRow
      .querySelector<HTMLElement>('[contenteditable="true"]')
      ?.focus();
    const flash = alpha(theme.palette.error.main, 0.16);
    const clear = alpha(theme.palette.error.main, 0);
    void animateSubjectLine(
      subjectLineRow,
      { backgroundColor: [clear, flash, clear, flash, clear] },
      { duration: 1.2 },
    );
  }, [subjectLineScope, savedSubjectLine, theme, animateSubjectLine]);

  useImperativeHandle(
    flushRef,
    () => ({
      flushPendingSubjectPreheader,
      confirmLeave,
      flagBlankSubjectLine,
    }),
    [flushPendingSubjectPreheader, confirmLeave, flagBlankSubjectLine],
  );

  const handleConfirmLinkedBroadcastsSave = useCallback(async () => {
    const pending = pendingSaveRef.current;
    if (!pending) return;
    try {
      await performSaveField(pending.change);
      pending.resolve();
    } catch (e) {
      pending.reject(e);
    } finally {
      pendingSaveRef.current = null;
      setIsLinkedBroadcastsModalOpen(false);
    }
  }, [performSaveField]);

  const handleCancelLinkedBroadcastsSave = useCallback(() => {
    // Reject so the inline field stays dirty/unsaved and the user can retry.
    pendingSaveRef.current?.reject();
    pendingSaveRef.current = null;
    setIsLinkedBroadcastsModalOpen(false);
  }, []);

  const runFieldSave = useCallback(
    async (change: SubjectPreheaderChange) => {
      setIsSavingField(true);
      try {
        await saveField(change);
      } finally {
        setIsSavingField(false);
      }
    },
    [saveField],
  );

  const { data: site } = useSuspenseQuery(siteQueryOptions(siteId));
  const { data: offersData } = useOfferBlockOffers(siteId);
  const offersList = useMemo(() => offersData?.offers ?? [], [offersData]);
  const { enqueueSnackbar } = useSnackbar();
  const lastPreviewErrorRef = useRef<string>("");

  const previewRequest = emailVariantPreviewRequest(state.selectedVariant);
  const previewPlaintext = previewRequest.contentPlaintext;
  const previewContent = previewRequest.contentText;
  const previewOffers = useMemo((): OffersConfig | undefined => {
    const variant = state.selectedVariant;
    const resolvedSubjectLine =
      testEmailPayloadOverrides?.subject ?? debouncedSubjectLine;
    const resolvedPreheader =
      testEmailPayloadOverrides?.preheader ?? debouncedPreviewText;
    const offers = buildPreviewOffersConfig({
      offersList,
      savedOffers: variant.config?.offers,
      bodyText: previewPlaintext ?? previewContent,
      subjectLine: resolvedSubjectLine,
      preheader: resolvedPreheader,
    });
    return Object.keys(offers).length > 0 ? offers : undefined;
  }, [
    debouncedPreviewText,
    debouncedSubjectLine,
    offersList,
    previewContent,
    previewPlaintext,
    state.selectedVariant,
    testEmailPayloadOverrides?.preheader,
    testEmailPayloadOverrides?.subject,
  ]);

  const previewConfig = useMemo((): Record<string, unknown> => {
    const variant = state.selectedVariant;
    const savedConfig =
      variant.config && typeof variant.config === "object"
        ? (variant.config as Record<string, unknown>)
        : {};

    const resolvedSubjectLine =
      testEmailPayloadOverrides?.subject ?? debouncedSubjectLine;
    const resolvedPreheader =
      testEmailPayloadOverrides?.preheader ?? debouncedPreviewText;

    const base: Record<string, unknown> = {
      frame_id: frame?.id ?? "",
      frame_variant_id: variant.id,
      ...savedConfig,
      subject_line: resolvedSubjectLine,
      preview_text: resolvedPreheader,
    };

    if (previewOffers) {
      base.offers = previewOffers;
    }

    return base;
  }, [
    debouncedPreviewText,
    debouncedSubjectLine,
    frame?.id,
    previewOffers,
    state.selectedVariant,
    testEmailPayloadOverrides?.preheader,
    testEmailPayloadOverrides?.subject,
  ]);

  const { data: renderedPreview, error: previewError } = useQuery({
    ...liveEmailPreviewQueryOptions(
      siteId,
      previewContent,
      previewConfig,
      undefined,
      previewPlaintext,
    ),
    select: previewRequest.selectDisplayHtml,
  });
  const previewHtml =
    renderedPreview || getEmailVariantPreviewHtml(state.selectedVariant);

  useEffect(() => {
    if (!previewError) {
      lastPreviewErrorRef.current = "";
      return;
    }

    const msg = getBackendErrorMessage(previewError);
    if (msg && msg !== lastPreviewErrorRef.current) {
      enqueueErrorSnackbar(previewError, "Error generating preview");
      lastPreviewErrorRef.current = msg;
    }
  }, [previewError]);

  const defaultTestEmailScope = {
    siteId,
    payload: {
      subject: subjectLine,
      body: getEmailVariantPreviewHtml(state.selectedVariant),
      plaintext: previewRequest.contentPlaintext,
      from_email: site?.default_from_email,
      reply_to: site?.default_reply_to,
      preheader: previewText,
      preheader_visible: false,
      from_name: site?.default_from_name,
      product_grids: state.selectedVariant.config?.product_grids,
      offers: previewOffers,
    },
  };

  const effectiveScope = {
    siteId: defaultTestEmailScope.siteId,
    payload: {
      ...defaultTestEmailScope.payload,
      ...(testEmailPayloadOverrides ?? {}),
    },
  };

  const { adapter: testEmailAdapter, scope: testEmailScope } = useTestEmail({
    siteId: defaultTestEmailScope.siteId,
    frameId: frame.id,
    frameVariantId: state.selectedVariant.id,
    basePayload: effectiveScope.payload,
    requireSubjectOrPreheader: true,
    onValidationError: (msg) => enqueueSnackbar(msg, { variant: "error" }),
  });

  return (
    <OuterWrapper
      sx={outerWrapperStyles}
      className={className}
      initial={{ x: 300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 300, opacity: 0 }}
      transition={{ type: "tween", duration: 0.3 }}
    >
      <TopBarWrapper>
        <CloseButton
          hideCloseButton={hideCloseButton}
          closeLink={closeLink}
          closeLinkParams={closeLinkParams}
          onClose={onClose}
        />
        {showSubjectAndPreHeaderInputs && (
          <Box
            sx={{
              background: (theme) =>
                theme.palette.background["paper-elevation-1"],
              display: "flex",
              flexDirection: "column",
              gap: 3,
              padding: "16px 40px",
            }}
          >
            <Box
              ref={subjectLineScope}
              sx={{ ...inputRowStyles, borderRadius: 1 }}
              data-testid="frame-editor-subject-line"
            >
              <Typography sx={{ color: "text.primary", width: 96 }}>
                Subject line
              </Typography>
              <InlineLexicalInput
                error={showSubjectLineError}
                helperText={
                  showSubjectLineError ? "Add a subject line" : undefined
                }
                value={subjectLine}
                savedValue={savedSubjectLine}
                offers={inlineOffers}
                onChange={setSubjectLine}
                onSave={(next) => runFieldSave({ subjectLine: next })}
                saveLabel="Save"
                saveDisabled={isSavingField}
              />
            </Box>
            <Box sx={inputRowStyles}>
              <Typography sx={{ color: "text.primary", width: 96 }}>
                Preheader
              </Typography>
              <InlineLexicalInput
                value={previewText}
                savedValue={savedPreviewText}
                offers={inlineOffers}
                onChange={setPreviewText}
                onSave={(next) => runFieldSave({ previewText: next })}
                saveLabel="Save"
                saveDisabled={isSavingField}
              />
            </Box>
          </Box>
        )}
        <TopBar>
          <Title flexGrow={1}>{title}</Title>
          {/* Edit Button - Only show for editable mediums with existing variants */}
          {showFullEditorButton && onFullEditorClick && (
            <Box sx={{ mr: 5 }}>
              <IconButton
                onClick={onFullEditorClick}
                sx={{ color: "secondary.main" }}
                size="medium"
                title="Edit tile"
              >
                <EditOutlinedIcon />
              </IconButton>
            </Box>
          )}
          {showReplaceButton && onReplaceClick && (
            <Button
              onClick={() =>
                void confirmLeave().then((leave) => {
                  if (leave) onReplaceClick();
                })
              }
              sx={{ color: "primary.main", mr: 2 }}
            >
              {replaceButtonText}
            </Button>
          )}
          {showTestEmailButton && (
            <TestEmailBar
              scope={testEmailScope}
              adapter={testEmailAdapter}
              siteId={siteId}
              buttonType="button"
            />
          )}
        </TopBar>
      </TopBarWrapper>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "text.primary",
        }}
      >
        <IframePreview
          // Avoid blank sandboxed previews after rapid srcdoc updates.
          key={previewHtml}
          sandbox=""
          srcDoc={previewHtml}
          title="Tile Preview"
          selectedDevice={selectedDevice}
        />
      </Box>
      <LinkedBroadcastsSaveConfirmModal
        open={isLinkedBroadcastsModalOpen}
        broadcasts={otherLinkedBroadcasts}
        isSaving={isPending}
        onCancel={handleCancelLinkedBroadcastsSave}
        onConfirm={() => void handleConfirmLinkedBroadcastsSave()}
      />
      <Dialog
        open={flushConfirmOpen}
        onClose={() => resolveFlushChoice("cancel")}
        aria-labelledby="flush-sl-ph-title"
      >
        <DialogTitle id="flush-sl-ph-title">
          Unsaved subject line / preheader
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Typography variant="body2" color="text.secondary">
              You have unsaved subject line or preheader changes.
            </Typography>
            {otherLinkedBroadcasts.length > 0 && (
              <>
                <Typography variant="body2" color="text.secondary">
                  This tile is also used by the broadcast(s) below. Saving your
                  edits will apply them to their upcoming sends.
                </Typography>
                <LinkedBroadcastsModalTable
                  broadcasts={otherLinkedBroadcasts}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ gap: 2, justifyContent: "flex-end", px: 4, pb: 3 }}
        >
          <Button color="inherit" onClick={() => resolveFlushChoice("cancel")}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => resolveFlushChoice("keep")}
          >
            Keep original copy
          </Button>
          <Button
            variant="contained"
            onClick={() => resolveFlushChoice("save")}
          >
            Save edits
          </Button>
        </DialogActions>
      </Dialog>
      <LeaveConfirmDialog
        open={leaveConfirmOpen}
        onCancel={() => resolveLeaveChoice(false)}
        onProceed={() => resolveLeaveChoice(true)}
        {...UNSAVED_SL_PH_LEAVE_COPY}
        // Switching tabs or tiles stays on the page, so don't say "Leave Page".
        proceedText="Discard changes"
      />
      <NavigationBlockerModal
        shouldBlock={hasUnsavedSubjectPreheader}
        enableBeforeUnload
        {...UNSAVED_SL_PH_LEAVE_COPY}
      />
    </OuterWrapper>
  );
}

export const FrameEditorWrapper = forwardRef<
  FrameEditorHandle,
  FrameEditorWrapperProps
>(function FrameEditorWrapper(props, ref) {
  const { frame, siteId } = props;
  return (
    <FrameEditorProvider
      key={frame.id}
      initialFrame={frame}
      siteId={siteId}
      onSave={props.onSave}
    >
      <FrameEditorContent {...props} flushRef={ref} />
    </FrameEditorProvider>
  );
});
